import { MutationCache } from '@tanstack/react-query'
import i18n from '@/i18n'
import { translateApiError } from '@/lib/apiError'
import { useNotificationStore } from '@/stores/useNotificationStore'

/**
 * WEB-H6 — global mutation error backstop.
 *
 * Roughly half of page-level `mutateAsync` call sites have no catch/toast, so
 * a failed save/delete showed the user nothing (button stops spinning, no
 * explanation). This MutationCache surfaces a translated error toast for ANY
 * failed mutation that nothing else surfaced.
 *
 * Double-toast avoidance: cache-level onError fires BEFORE the mutation
 * promise rejects, so local `try { await mutateAsync() } catch { addToast }`
 * handlers run in the microtasks after us. We defer one macrotask and only
 * toast if no error toast appeared in between — local handlers (with their
 * namespace-specific messages) win; the backstop covers the silent rest.
 *
 * Opt-out for mutations that surface errors some other way (inline form
 * errors, dedicated error UI): pass `meta: { suppressErrorToast: true }` to
 * useMutation.
 */
export function createMutationCacheWithErrorBackstop(): MutationCache {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.suppressErrorToast) return

      const errorToastCount = () =>
        useNotificationStore.getState().toasts.filter((t) => t.type === 'error').length
      const before = errorToastCount()

      setTimeout(() => {
        if (errorToastCount() > before) return // a local handler already surfaced it

        const tc = i18n.getFixedT(null, 'common')
        useNotificationStore.getState().addToast({
          type: 'error',
          message: translateApiError(error, 'common', { t: tc, tc }),
        })
      }, 0)
    },
  })
}
